// apps/web/src/App.tsx
// Top-level layout shell + route outlet (per Plan §4.6.1 line 3469).
// Step 22.B: minimal shell. Step 22.D wires <RequireAuth/> guard at
// the router level (beforeLoad), not here.
//
// 2026-05-24 PM — per-route document.title via getPageTitle(pathname);
// index.html ships static <title>Daily Brief</title> as the boot title
// (visible for the few ms before React mounts); App immediately overrides
// based on location.
//
// 2026-05-26 §K.17 — body[data-theme] switch. /brief + /signal-history
// opt into the dense theme defined in theme/v2-dense.css; everything
// else stays on the default :root theme (theme/index.css). Rollback =
// change the route → theme map below.

import { useEffect } from 'react';
import { Outlet, useLocation } from '@tanstack/react-router';
import { getPageTitle } from './lib/product.js';
import { NotificationBell } from './primitives/NotificationBell.js';

// Route prefixes that opt into the v2-dense theme. Order doesn't matter —
// startsWith() match. Sub-routes inherit (e.g. /brief/abc123).
// Paths use the current route names (router/routes.tsx): /signal-history,
// /operations, /crypto. The dense theme applies to /brief + /signal-history
// only (per IA design notes §7); /crypto + /operations stay on the default
// theme until they migrate.
const V2_DENSE_PREFIXES = ['/brief', '/signal-history'] as const;

function themeForPath(pathname: string): 'v2-dense' | 'default' {
  return V2_DENSE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
    ? 'v2-dense'
    : 'default';
}

export default function App() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = getPageTitle(pathname);
    document.body.dataset.theme = themeForPath(pathname);
  }, [pathname]);
  // Ops Notices — global notification bell + critical banner across every
  // authenticated dashboard. Gated off the un-authed /sign-in route (the
  // /api/notifications fetch needs the session cookie).
  const showNotifications = pathname !== '/sign-in';
  return (
    <>
      <Outlet />
      <NotificationBell enabled={showNotifications} />
    </>
  );
}
